import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextField, Button, Typography, Box, Card, InputAdornment, IconButton, Alert } from '@mui/material';
import { handleResetPassword as resetPasswordApi } from '../actions/auth';
import { useAlert } from '../contexts/AlertContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { showAlert } = useAlert();

  // Password validation function (same as Settings page)
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  // Check if password meets all requirements
  const isPasswordValid = (password) => {
    return validatePassword(password).length === 0;
  };

  // Handle password field blur
  const handlePasswordBlur = () => {
    if (password && !isPasswordValid(password)) {
      setShowPasswordRequirements(true);
    } else {
      setShowPasswordRequirements(false);
    }
  };

  // Handle password field focus
  const handlePasswordFocus = () => {
    setShowPasswordRequirements(false);
  };

  // Get password requirements message
  const getPasswordRequirementsMessage = () => {
    const requirements = [];
    
    if (password.length < 8) {
      requirements.push('at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      requirements.push('one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      requirements.push('one lowercase letter');
    }
    if (!/\d/.test(password)) {
      requirements.push('one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      requirements.push('one special character');
    }
    
    return requirements.join(', ');
  };

  const handleResetPassword = async () => {
    if (!token) {
      showAlert('Invalid or missing reset link. Please request a new password reset.', 'error');
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    // Validate password requirements
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setPasswordError(`Password requirements not met: ${passwordErrors.join(', ')}`);
      return;
    }
    
    // Clear any previous password errors
    setPasswordError('');
    
    setIsLoading(true);
    try {
      await resetPasswordApi(token, password);
      showAlert('Password updated successfully. Redirecting to login...', 'success');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      showAlert('Failed to reset password. The link may have expired.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <Box sx={{ minHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: (theme) => theme.palette.mode === 'dark' ? '#121212' : '#ffffff' }}>
        <Card sx={{ maxWidth: 400, width: '100%', p: 4, borderRadius: 4, boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 2 }}>
            Invalid Reset Link
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            This password reset link is invalid or missing. Reset links expire after 15 minutes.
          </Alert>
          <Button
            onClick={() => navigate('/login')}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ py: 1.5, borderRadius: 2, fontWeight: 600 }}
          >
            Back to Login
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: (theme) => theme.palette.mode === 'dark' ? '#121212' : '#ffffff' }}>
      <Card sx={{ maxWidth: 400, width: '100%', p: 4, borderRadius: 4, boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 2 }}>
          Reset Password
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Enter your new password below. This link expires in 15 minutes.
        </Typography>
        <TextField
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          margin="normal"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          onBlur={handlePasswordBlur}
          onFocus={handlePasswordFocus}
          tabIndex={1}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((show) => !show)}
                  edge="end"
                  tabIndex={-1}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.02)',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)' 
                  : 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-focused': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.06)',
              }
            }
          }}
        />

        {/* Password Requirements Alert */}
        {showPasswordRequirements && (
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            Password must contain: {getPasswordRequirementsMessage()}
          </Alert>
        )}

        <TextField
          label="Confirm New Password"
          type={showConfirmPassword ? 'text' : 'password'}
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={!!passwordError}
          helperText={passwordError}
          required
          tabIndex={2}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword((show) => !show)}
                  edge="end"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.02)',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)' 
                  : 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-focused': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.06)',
              }
            }
          }}
        />
        <Button
          onClick={handleResetPassword}
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 3, py: 1.5, borderRadius: 2, fontWeight: 600, fontSize: '1.1rem' }}
          disabled={isLoading || !password || !confirmPassword || !isPasswordValid(password)}
          tabIndex={3}
        >
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </Button>
        
        <Button
          onClick={() => navigate('/login')}
          variant="text"
          color="primary"
          fullWidth
          sx={{ 
            mt: 2, 
            py: 1.5, 
            borderRadius: 2, 
            fontWeight: 500, 
            fontSize: '1rem',
            textTransform: 'none'
          }}
          tabIndex={4}
        >
          Back to Login
        </Button>
      </Card>
    </Box>
  );
};

export default ResetPassword;